import { useGlobalModal } from "@/hooks/useGlobalModal";
import GlobalModal from "@components/GlobalModal";
import ModalContent from "@components/modal/ModalContent";
import { msg, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Box, Button, CircularProgress, DialogActions, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import MetricSelectionStep from "./MetricSelectionStep";
import DeviceSelectionStep from "./DeviceSelectionStep";
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import UserSelectionStep, { UserNotificationPreference } from "./UserSelectionStep";
import { useLazyQuery } from "@apollo/client";
import SummaryStep from "./SummaryStep/SummaryStep";
import { AlertConfigurationForm, alertConfigurationSchema } from "@features/alertsConfigurationV2/schemas/alertConfigurationSchema";
import { useCreateAlertConfiguration } from "@features/alertsConfigurationV2/hooks/useCreateAlertConfiguration";
import { Device, GET_DEVICE_CONFIGURABLE_METRICS } from "@features/alertsConfigurationV2/graphql/queries/getDeviceConfigurableMetrics";
import ParametersSelectionStep from "./ParametersSelectionStep";
import { MetricUnit } from "@/__generated__/graphql";


interface UserPreference {
  id: number;
  name: string;
  email: string;
  useEmail: boolean;
  usePush: boolean;
  useSms: boolean;
  useWhatsapp: boolean;
}

const STEP_CONFIG = [
  {
    label: msg`Dispositivo`,
    validationField: 'deviceId' as const,
    component: DeviceSelectionStep,
  },
  {
    label: msg`Métricas`,
    validationField: 'metric' as const,
    component: MetricSelectionStep,
  },
  {
    label: msg`Rangos`,
    validationField: ['configurations', 'unit'] as const,
    component: ParametersSelectionStep,
  },
  {
    label: msg`Destinatarios`,
    validationField: 'usersToNotify' as const,
    component: UserSelectionStep,
  },
  {
    label: msg`Resumen general`,
    validationField: null,
    component: SummaryStep,
  },
] as const;

export const AlertsConfigurationModal: FC = () => {
  const { _ } = useLingui();
  const methods = useForm<AlertConfigurationForm>({
    resolver: zodResolver(alertConfigurationSchema),
    defaultValues: {
      deviceId: '',
      metric: '',
      unit: MetricUnit.None,
      configurations: [],
      usersToNotify: [],
    },
  });

  const { trigger, watch, setValue, reset } = methods;
  const { onSubmit: createAlertConfig } = useCreateAlertConfiguration();
  const { globalModalProps, state, onClose } = useGlobalModal('alertsConfiguration');
  const [activeStep, setActiveStep] = useState(0);
  const [users, setUsers] = useState<UserPreference[] | null>(null);
  const [userPreferences, setUserPreferences] = useState({current: new Map()});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePreferencesChange = (current: Map<string, UserNotificationPreference>) => {
    setUserPreferences({ current });
  }


  const [getDeviceData, result] = useLazyQuery(GET_DEVICE_CONFIGURABLE_METRICS, {
    fetchPolicy: 'no-cache',
  });
  let deviceData: Device | undefined = result.data?.device;

  const deviceId = watch('deviceId');
  const selectedMetric = watch('metric');

  useEffect(() => {
    if (state.open) {
      if ('deviceId' in state && state.deviceId) {
        setValue('deviceId', state.deviceId);
      }

      if ('step' in state && typeof state.step === 'number') {
        setActiveStep(state.step);
      }
    }
  }, [state.open]);

  useEffect(() => {
    if (deviceId) {
      console.log('popote....')
      getDeviceData({ variables: { input: { id: deviceId } } });
      deviceData = result.data?.device;
    }
  }, [deviceId, getDeviceData]);

  useEffect(() => {
    if (deviceData?.users && deviceData?.users.length > 0) {
      const currentUsers = deviceData.users.map((userEntry) => ({
        id: userEntry.user.id,
        name: userEntry.user.fullName,
        email: userEntry.user.email,
        useEmail: userEntry.alertNotificationConfiguration?.notifyEmail ?? false,
        usePush: userEntry.alertNotificationConfiguration?.notifyPush ?? false,
        useSms: userEntry.alertNotificationConfiguration?.notifySms ?? false,
        useWhatsapp: userEntry.alertNotificationConfiguration?.notifyWhatsapp ?? false,
      }));
      setUsers(currentUsers);
    }
  }, [deviceData])

  const handleClose = () => {
    reset();
    setActiveStep(0);
    onClose();
  }


  const handleNext = async () => {
    const currentStepConfig = STEP_CONFIG[activeStep];
    
    if (currentStepConfig.validationField) {
      const isValid = await trigger(currentStepConfig.validationField);
      if (!isValid) return;
    }

    setActiveStep(prevStep => prevStep + 1);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const data = methods.getValues();
      const success = await createAlertConfig(data);

      if (success) {
        setTimeout(() => {
          handleClose();
        }, 500);
      }
    } catch (error) {
      console.error('Error creating alert configuration:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <DeviceSelectionStep />
        );
      case 1:
        return (
          <MetricSelectionStep
            deviceProfile={deviceData?.profile}
            metrics={deviceData?.alerts?.alertConfigurations ?? []}
          />
        );
      case 2:
        return (
          <ParametersSelectionStep
            metric={deviceData?.alerts.alertConfigurations.find((config) => config.metricName === selectedMetric) ?? null}
          />
        );
      case 3:
        return (
          <UserSelectionStep
            users={users ?? []}
            onPreferencesChange={handlePreferencesChange}
          />
        );
      case 4:
        return (
          <SummaryStep
            deviceProfile={deviceData?.profile}
            userPreferences={userPreferences}
          />
        );
      default:
        return null;
    }
  };

  const isLastStep = activeStep === STEP_CONFIG.length - 1;


  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'lg' }} onClose={handleClose}>
      {state.open && (
        <FormProvider {...methods}>
          <Box p={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
              <Typography variant='h5' sx={{ pl: 2 }}>
                <Trans>Configuración de alertas</Trans>
              </Typography>
              {' - '}
              <Typography fontSize={24}>Paso N°{activeStep + 1}</Typography>
            </Box>
            <FormResetHandler />

            <ModalContent dividers={false} sx={{ p: 0 }}>
              <Stepper 
                activeStep={activeStep}
                connector={null}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${STEP_CONFIG.length}, 1fr)`,
                    width: 1
                    
                  }}
                >
                  {STEP_CONFIG.map((step, index) => {
                    const active = index === activeStep;
                    return (
                    <Step key={index} sx={{ p: 0 }}>
                      <StepLabel>
                      <Box
                        key={index}
                        sx={{
                          py: 1,
                          px: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          borderWidth: active ? '1px 1px 0 1px' : '0 0 1px 0',
                          borderColor: 'white',
                          borderStyle: 'solid',
                          borderTopLeftRadius: 8,
                          borderTopRightRadius: 8,
                        }}
                      >
                        <Box
                          width={22}
                          height={22}
                          borderRadius={20}
                          bgcolor={index > activeStep ? 'text.secondary' : 'white'}
                          sx={{
                            color: 'black',
                            display: 'grid',
                            placeItems: 'center',
                          }}
                        >
                          <Typography fontWeight="medium" lineHeight={1} fontSize={14}>
                            {index + 1}
                          </Typography>
                        </Box>
                        <Typography fontWeight="medium" color={index > activeStep ? 'text.secondary' : 'white'}>{_(step.label)}</Typography>
                      </Box>
                      </StepLabel>
                    </Step>
                  )})}
                </Box>
              </Stepper>

              <Box width='100%' minHeight='78dvh'
                sx={{
                  borderWidth: '0px 1px 1px 1px',
                  borderColor: 'white',
                  borderStyle: 'solid',
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  px: 5,
                  pt: 2,
                  pb: 1,
                }}>
                {renderStepContent()}
                <DialogActions>
                  <Button
                    variant='outlined'
                    disabled={activeStep === 0}
                    onClick={handleBack}
                  >
                    Anterior
                  </Button>
                  {isLastStep ? (
                    <Button
                      variant='contained'
                      sx={{ backgroundColor: '#89B366' }}
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      Confirmar
                    </Button>
                  ) : (
                    <Button variant='contained' onClick={handleNext}>
                      Siguiente
                    </Button>
                  )}
                </DialogActions>
              </Box>
            </ModalContent>

          </Box>
        </FormProvider>
      )}
    </GlobalModal>
  );
};

const FormResetHandler = () => {
  useFormReset();
  return null;
};

export const useFormReset = () => {
  const { watch, setValue, resetField } = useFormContext<AlertConfigurationForm>();

  const deviceId = watch('deviceId');
  const metric = watch('metric');

  const previousDeviceId = useRef<string | undefined>(undefined);
  const previousMetric = useRef<string | undefined>(undefined);

  // Al cambiar el dispositivo, resetear métrica, configuraciones y usuarios
  useEffect(() => {
    if (previousDeviceId.current === undefined) {
      previousDeviceId.current = deviceId;
      return;
    }

    if (previousDeviceId.current !== deviceId && deviceId) {
      setValue('metric', '');
      setValue('configurations', []); 
      setValue('usersToNotify', []);
      previousDeviceId.current = deviceId;
    }
  }, [deviceId]);

  // Al cambiar la métrica, resetear configuraciones
  useEffect(() => {
    if(previousMetric.current === undefined) {
      previousMetric.current = metric;
      return;
    }

    if (previousMetric.current !== metric && metric) {
      setValue('configurations', []);
      previousMetric.current = metric;
    }
  }, [metric]);

  return {
    deviceId,
    metric,
  };
}